import { create } from 'zustand';
import { TASK_EDITING_PING_MS, type TaskEditingClientMessage, type TaskEditor } from '@shared/types/tasks';

/**
 * Who has a task's description open to edit (T-46), from the live socket, and what this tab is
 * editing, which TaskLiveConnector sends the hub (again on every connect). The hub forgets a tab's
 * edit when its socket closes, so a closed tab or a lost connection never leaves a task blocked.
 * A tab hidden for TASK_EDITING_HIDDEN_MS lets go too, keeping its draft, and says it's editing
 * again when it's shown.
 */

/** A tab in the background this long stops blocking others. */
export const TASK_EDITING_HIDDEN_MS = 2 * 60_000;

/** This tab's id: the hub tells a person's other tabs apart by it. */
export const TAB_ID = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);

/** Another tab's edit, with when it last typed by this tab's clock. */
export interface TaskEditorSeen {
  user_id: string;
  tab_id: string;
  task_id: string;
  active_at: number;
}

interface TaskEditingState {
  /** Everyone's edits but this tab's; empty while the socket is down. */
  editors: TaskEditorSeen[];
  /** The task whose description this tab is editing. */
  mine: string | null;
  /** When this tab last told the hub it's editing. */
  sentAt: number;
  /** When the last list arrived (this tab's clock). */
  receivedAt: number;
}

export const useTaskEditingStore = create<TaskEditingState>()(() => ({ editors: [], mine: null, sentAt: 0, receivedAt: 0 }));

/** The live socket's send, while it's open (TaskLiveConnector). */
let sender: ((text: string) => void) | null = null;
/** Hidden long enough to let go: the hub hears this tab isn't editing, though `mine` stays. */
let away = false;
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;

/** What the hub should hear this tab is editing. */
function announced(): string | null {
  return away ? null : useTaskEditingStore.getState().mine;
}

function send(taskId: string | null): void {
  const message: TaskEditingClientMessage = { type: 'editing', tab_id: TAB_ID, task_id: taskId };
  sender?.(JSON.stringify(message));
  useTaskEditingStore.setState({ sentAt: Date.now() });
}

/** The socket opened (it says what this tab is editing, and hears who else is) or closed (null). */
export function setEditingSender(next: ((text: string) => void) | null): void {
  sender = next;
  if (next) send(announced());
  else useTaskEditingStore.setState({ editors: [] });
}

/** The hub's list of who's editing. */
export function receiveEditors(editors: TaskEditor[]): void {
  const now = Date.now();
  useTaskEditingStore.setState({
    receivedAt: now,
    editors: editors
      .filter((e) => e.tab_id !== TAB_ID)
      .map((e) => ({ user_id: e.user_id, tab_id: e.tab_id, task_id: e.task_id, active_at: now - e.idle_ms })),
  });
}

export function startEditing(taskId: string): void {
  useTaskEditingStore.setState({ mine: taskId });
  send(announced());
}

/** Typing: tells the hub you're still there, at most every TASK_EDITING_PING_MS. */
export function stillEditing(taskId: string): void {
  const { mine, sentAt } = useTaskEditingStore.getState();
  if (mine === taskId && !away && Date.now() - sentAt >= TASK_EDITING_PING_MS) send(taskId);
}

export function stopEditing(taskId: string): void {
  if (useTaskEditingStore.getState().mine !== taskId) return;
  useTaskEditingStore.setState({ mine: null });
  send(null);
}

/**
 * The tab was hidden or shown. Hidden for TASK_EDITING_HIDDEN_MS, it tells the hub it stopped
 * editing (the draft stays open); shown again, it says it's editing once more.
 */
export function tabVisibilityChanged(hidden: boolean): void {
  if (hiddenTimer) clearTimeout(hiddenTimer);
  hiddenTimer = null;
  if (hidden) {
    hiddenTimer = setTimeout(() => {
      hiddenTimer = null;
      away = true;
      if (useTaskEditingStore.getState().mine) send(null);
    }, TASK_EDITING_HIDDEN_MS);
    return;
  }
  if (!away) return;
  away = false;
  if (useTaskEditingStore.getState().mine) send(announced());
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => tabVisibilityChanged(document.hidden));
}
